import { useState, useEffect, useCallback } from 'react';
import { teamAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Users, TrendingUp, ShieldAlert } from 'lucide-react';

const PERIODS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const StatCard = ({ label, value, sub, icon: Icon, testId }) => (
  <Card className="rounded-sm">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-2xl font-semibold mt-1" data-testid={testId}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
      </div>
    </CardContent>
  </Card>
);

export default function TeamDashboardPage() {
  const { user } = useAuth();
  const [days, setDays] = useState('30');
  const [scope, setScope] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamAPI.summary({ days: Number(days), scope });
      setData(res.data);
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [days, scope]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const openMember = async (row) => {
    setDetailLoading(true);
    setDetail({ member: { name: row.name, email: row.email, crm_role_name: row.crm_role_name } });
    try {
      const res = await teamAPI.member(row.user_id, { days: Number(days) });
      setDetail(res.data);
    } catch {
      toast.error('Failed to load member records');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (denied) {
    return (
      <Card className="rounded-sm" data-testid="team-no-access">
        <CardContent className="p-10 text-center space-y-2">
          <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            My Team is for managers — nobody reports to you yet in the org chart.
          </p>
        </CardContent>
      </Card>
    );
  }

  const cards = data?.cards;

  return (
    <div className="space-y-6" data-testid="team-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My Team</h1>
          <p className="text-sm text-muted-foreground">
            {data?.org_wide
              ? `Organisation-wide view · ${cards?.members ?? 0} people`
              : `${user?.name}'s rollup · ${cards?.members ?? 0} people · ${data?.direct_count ?? 0} direct report(s)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope} disabled={data?.org_wide}>
            <SelectTrigger className="w-[210px] rounded-sm" data-testid="team-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct">My direct reports only</SelectItem>
              <SelectItem value="all">Whole team (incl. indirect)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px] rounded-sm" data-testid="team-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label="Bids" value={cards.bids} sub={`${cards.bids_submitted} submitted`} icon={TrendingUp} testId="team-card-bids" />
            <StatCard
              label="Bid Outcomes"
              value={`${cards.buy} / ${cards.negotiate} / ${cards.reject}`}
              sub="buy / negotiate / reject"
              testId="team-card-outcomes"
            />
            <StatCard label="Coverage" value={`${cards.vendors} / ${cards.customers}`} sub="vendors / customers" icon={Users} testId="team-card-coverage" />
          </div>

          <Card className="rounded-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-medium text-xs uppercase">Person</TableHead>
                    <TableHead className="font-medium text-xs uppercase">Zoho Role</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Bids</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Vendors</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Customers</TableHead>
                    <TableHead className="font-medium text-xs uppercase">Last Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.members.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No team members in this scope</TableCell></TableRow>
                  ) : data.members.map((row) => (
                    <TableRow
                      key={row.user_id}
                      className="table-row-hover cursor-pointer"
                      onClick={() => openMember(row)}
                      data-testid={`team-row-${row.email}`}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          {row.name}
                          {row.is_direct && !data.org_wide && <Badge variant="outline" className="text-[10px] rounded-sm font-normal">direct</Badge>}
                        </span>
                        <span className="text-xs text-muted-foreground">{row.email}</span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.crm_role_name || '—'}</TableCell>
                      <TableCell className="text-right text-sm">{row.bids}</TableCell>
                      <TableCell className="text-right text-sm">{row.vendors}</TableCell>
                      <TableCell className="text-right text-sm">{row.customers}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.last_activity ? new Date(row.last_activity).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{detail?.member?.name}</DialogTitle>
            <DialogDescription>
              {detail?.member?.crm_role_name || '—'} · {detail?.member?.email} · last {days} days
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <ScrollArea className="h-[340px]">
              {(detail?.bids || []).length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No bids in this period</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs uppercase">Vendor</TableHead>
                    <TableHead className="text-xs uppercase">Bid No.</TableHead>
                    <TableHead className="text-xs uppercase">Status</TableHead>
                    <TableHead className="text-xs uppercase">Date</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {detail.bids.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-sm">{b.vendor_name_canonical || b.vendor_name || '—'}</TableCell>
                        <TableCell className="text-sm">{b.bid_number ? String(b.bid_number).padStart(5, '0') : '—'}</TableCell>
                        <TableCell><Badge variant="outline" className="rounded-sm text-xs">{b.submitted ? 'SUBMITTED' : (b.status || '').toUpperCase()}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
